
import { supabase } from "@/integrations/supabase/client";

// Function to finalize the game ranking (making it ready for the next day's stats view)
export async function finalizeGameRanking(gameId: string): Promise<boolean> {
  try {
    if (!gameId) {
      console.warn("[Backend] No game ID provided for finalization");
      return false;
    }
    
    console.log("[Backend] Finalizing game ranking for game:", gameId);
    
    // First check if the game is already finalized
    const { data: gameData, error: gameError } = await supabase
      .from('games')
      .select('final_ranking, is_closed')
      .eq('id', gameId)
      .single();
      
    if (gameError) {
      console.error("[Backend] Error checking game status:", gameError);
      return false;
    }
    
    // If game is already finalized, no need to do anything
    if (gameData.is_closed && gameData.final_ranking) {
      console.log("[Backend] Game already finalized");
      return true;
    }
    
    // Get the current live stats for this game
    const { data: liveStats, error: statsError } = await supabase
      .from('live_stats')
      .select('word_rankings, participant_count')
      .eq('game_id', gameId)
      .maybeSingle();
      
    if (statsError || !liveStats) {
      console.error("[Backend] Error getting live stats:", statsError);
      return false;
    }
    
    // Update the game with the final ranking
    const { error: updateError } = await supabase
      .from('games')
      .update({
        final_ranking: liveStats.word_rankings,
        participant_count: liveStats.participant_count || 0,
        // Only mark as closed if it's for a past date
        // We'll have a background job that marks games as closed automatically
      })
      .eq('id', gameId);
      
    if (updateError) {
      console.error("[Backend] Error updating game with final ranking:", updateError);
      return false;
    }
    
    console.log("[Backend] Successfully finalized game ranking");
    return true;
  } catch (error) {
    console.error("[Backend] Error in finalizeGameRanking:", error);
    return false;
  }
}

// Function to save a user's submission for a game
export async function saveUserSubmission(
  gameId: string,
  userId: string,
  ranking: string[]
): Promise<boolean> {
  try {
    if (!gameId || !userId || !ranking || !Array.isArray(ranking)) {
      console.error("[Backend] Invalid parameters for saveUserSubmission");
      return false;
    }
    
    console.log("[Backend] Saving submission for user:", userId, "game:", gameId);
    console.log("[Backend] Ranking to save:", ranking);
    
    // First check if user already submitted for this game
    const { data: existingSubmission, error: checkError } = await supabase
      .from('user_submissions')
      .select('id')
      .eq('game_id', gameId)
      .eq('user_id', userId)
      .maybeSingle();
      
    if (checkError) {
      console.error("[Backend] Error checking existing submission:", checkError);
      // Continue anyway, worst case we'll create a duplicate
    }
    
    if (existingSubmission) {
      console.log("[Backend] User already submitted for this game, updating submission");
      
      // Update existing submission
      const { error: updateError } = await supabase
        .from('user_submissions')
        .update({ ranking })
        .eq('id', existingSubmission.id);
        
      if (updateError) {
        console.error("[Backend] Error updating submission:", updateError);
        return false;
      }
    } else {
      // Create new submission
      console.log("[Backend] Creating new user submission");
      
      // Direct insert without using RPC
      const { error: insertError } = await supabase
        .from('user_submissions')
        .insert({ 
          game_id: gameId, 
          user_id: userId, 
          ranking 
        });
        
      if (insertError) {
        console.error("[Backend] Error creating submission:", insertError);
        return false;
      }
    }
    
    // Also update the live_stats table to ensure our submission is counted
    try {
      // Need to provide word_rankings as it's required
      const { error: liveStatsError } = await supabase
        .from('live_stats')
        .upsert({
          game_id: gameId,
          participant_count: 1, // Will be incremented by trigger
          updated_at: new Date().toISOString(),
          word_rankings: ranking // Add the required field
        }, { 
          onConflict: 'game_id'
        });
        
      if (liveStatsError) {
        console.log("[Backend] Error updating live stats (non-critical):", liveStatsError);
      }
    } catch (statsErr) {
      console.log("[Backend] Error in live stats update (non-critical):", statsErr);
    }
    
    console.log("[Backend] Successfully saved user submission");
    return true;
  } catch (error) {
    console.error("[Backend] Error saving user submission:", error);
    return false;
  }
}

// Helper function to validate ranking format
export function validateRanking(ranking: any): ranking is string[] {
  if (!Array.isArray(ranking)) return false;
  if (ranking.length === 0) return false;
  return ranking.every(item => typeof item === 'string');
}
